import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import pluralize from 'pluralize'
import { useEffect, useRef, useState } from 'react'

import { DefaultProjectImage } from 'src/components/atoms/DefaultProjectImage'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { BlogPostCardRebrand } from 'src/components/molecules/BlogPostCardRebrand'
import { PaginationRebrand } from 'src/components/molecules/PaginationRebrand'
import { ProjectCardRebrand } from 'src/components/molecules/ProjectCardRebrand'
import { Tabs } from 'src/components/molecules/Tabs'

import { useListAccountSupportersLazyQuery } from 'src/config/generated/graphql'
import { viewProjectPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { usePagination } from 'src/hooks/usePagination'
import {
  PostsByPlanterQueryResult,
  PostsByFunderQueryResult,
  PostsQueryPost
} from 'src/server/router/sanity/groq/post/types'
import { trpc } from 'src/utils/trpc'

import { AccountSupportTabsState } from './types'

export interface AccountSupportStatsProps {
  accountId: string
  name: string
  planterPosts?: PostsByPlanterQueryResult
  funderPosts?: PostsByFunderQueryResult
}

export enum AccountSupportTabs {
  SupportGiven = 'supportGiven',
  Stories = 'stories'
}

export const AccountSupportStats = ({
  accountId,
  name,
  planterPosts,
  funderPosts
}: AccountSupportStatsProps) => {
  const router = useRouter()
  const [tabConfig, setTabConfig] = useState<AccountSupportTabsState>({
    tabs: []
  })

  const { limit, offset, onPageClick, currentPage, resetPagination } =
    usePagination({ limit: 4 })

  useEffect(() => {
    const { activeTab } = router.query
    if (activeTab)
      setTabConfig(prev => ({ ...prev, activeTab: activeTab as string }))
  }, [router.query])

  const posts = [...(planterPosts || []), ...(funderPosts || [])].sort(
    (a: PostsQueryPost, b: PostsQueryPost) =>
      dayjs(b.publishedAt).diff(dayjs(a.publishedAt))
  )

  const [listAccountSupporters, { data: supportersData, loading }] =
    useListAccountSupportersLazyQuery()

  const { data: supportedProjects } = trpc.useQuery(
    [
      'projects.getSupportedProjectsAggregated',
      {
        limit,
        offset,
        funderId: accountId,
        orderBy: 'LatestFundingDate_desc'
      }
    ],
    { refetchOnWindowFocus: false }
  )

  const supportGivenTotalCount = supportedProjects?.count || 0

  useEffect(() => {
    if (accountId) {
      listAccountSupporters({ variables: { accountId, offset } })
      setTabConfig({
        tabs: [],
        activeTab: ''
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, offset])
  useEffect(() => {
    if (!supportersData) return

    const tabsToShow: {
      label: string
      value: string
      order: number
    }[] = []
    if (supportGivenTotalCount) {
      tabsToShow.push({
        label: 'Support given',
        value: AccountSupportTabs.SupportGiven,
        order: supportGivenTotalCount
      })
    }
    if (posts?.length) {
      tabsToShow.push({
        label: 'Stories',
        value: AccountSupportTabs.Stories,
        order: 0
      })
    }
    const sortedTabs = tabsToShow.sort((a, b) => b.order - a.order)
    setTabConfig(prev => ({
      tabs: sortedTabs,
      activeTab: prev.activeTab || sortedTabs[0]?.value
    }))
  }, [supportGivenTotalCount, posts?.length, supportersData])

  const handleChangeTab = (tab: string) => {
    if (tab === tabConfig.activeTab) return

    setTabConfig(prev => ({ ...prev, activeTab: tab }))
    resetPagination()
  }

  const containerRef = useRef<HTMLDivElement>(null)
  return (
    <>
      <div ref={containerRef} className='align-center'>
        <Tabs
          className='mt-2 w-full'
          tabs={tabConfig.tabs}
          onChange={handleChangeTab}
          activeTab={tabConfig.activeTab as AccountSupportTabs}
          ariaLabel='Account support tabs'
        />
      </div>
      <LoadingSpinner isLoading={loading}>
        {tabConfig.activeTab === AccountSupportTabs.SupportGiven &&
          supportGivenTotalCount > 0 && (
            <>
              <TypographyRebrand variant='h4' color='espresso'>
                Projects supported
              </TypographyRebrand>
              <TypographyRebrand variant='body' color='espresso'>
                {name} has supported{' '}
                {pluralize('project', supportGivenTotalCount, true)}.
              </TypographyRebrand>
              {supportedProjects?.projects?.length ? (
                <>
                  <div className='grid grid-cols-1 md:grid-cols-2 md:flex-wrap flex-col md:flex-row gap-5 auto-rows-auto'>
                    {supportedProjects?.projects.map((project, index) => (
                      <div
                        key={project.ProjectId}
                        data-testid={`supportedProject${index}`}
                      >
                        <ProjectCardRebrand
                          name={project.Name ?? ''}
                          imageUrl={
                            project.PublicPage
                              ? `${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${project.Hero}`
                              : undefined
                          }
                          location={project.Region?.Name || undefined}
                          treeCount={project.Quantity || undefined}
                          href={
                            project.PublicPage && project.Uri
                              ? viewProjectPath({
                                  routeParams: { uri: project.Uri }
                                })
                              : undefined
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className='flex-row flex-wrap flex justify-between'>
                    <PaginationRebrand
                      page={currentPage}
                      pageSize={limit}
                      onChange={(page: number) => onPageClick(page)}
                      count={supportGivenTotalCount}
                    />
                    {supportedProjects?.projects.find(p => !p.PublicPage) && (
                      <div className='flex flex-row gap-4 items-center'>
                        <DefaultProjectImage className='w-[40px] h-[40px] rounded-full' />
                        Planting projects without publicly displayed pages.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div>We could not find any projects</div>
              )}
            </>
          )}
        {tabConfig.activeTab === AccountSupportTabs.Stories && (
          <>
            <TypographyRebrand variant='h4' color='espresso'>
              News & stories
            </TypographyRebrand>
            <TypographyRebrand variant='body' color='espresso'>
              Read about how {name} has made an impact.
            </TypographyRebrand>
            <div className='grid gap-10 md:grid-cols-1 lg:grid-cols-2'>
              {posts?.map(post => {
                return (
                  <BlogPostCardRebrand key={post.slug.current} post={post} />
                )
              })}
            </div>
          </>
        )}
      </LoadingSpinner>
    </>
  )
}
